import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { BarChart, Clock } from 'lucide-react';
import BottomNav from '../components/BottomNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const Week = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWeekStats();
  }, []);

  const fetchWeekStats = async () => {
    try {
      const response = await axios.get(`${API}/time-entries/week`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch week stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 50%, #80deea 100%)' }}>
        <div className="inline-block w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const maxProjectMinutes = Math.max(...Object.values(stats?.by_project || {}), 1);

  return (
    <div className="min-h-screen pb-24" style={{ background: 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 50%, #80deea 100%)' }}>
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md shadow-md">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: 'Space Grotesk' }} data-testid="page-title">Week Overview</h1>
          <p className="text-gray-600">Last 7 days</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Total Summary */}
        <Card className="bg-gradient-to-br from-cyan-500 to-teal-500 text-white shadow-xl mb-6 border-0" data-testid="week-summary">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-cyan-100 text-sm mb-1">Weekly Total</p>
                <h2 className="text-4xl font-bold" style={{ fontFamily: 'Space Grotesk' }} data-testid="week-total">
                  {stats?.total_hours || 0}h
                </h2>
              </div>
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <Clock className="w-8 h-8" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-cyan-100">
              <BarChart className="w-4 h-4 mr-1" />
              <span className="text-sm">{stats?.entry_count || 0} total entries</span>
            </div>
          </div>
        </Card>

        {/* By Project */}
        <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0 mb-6" data-testid="by-project-card">
          <div className="p-6">
            <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk' }}>Time by Project</h3>
            {stats && Object.keys(stats.by_project).length === 0 ? (
              <p className="text-gray-600 text-center py-8">No entries this week</p>
            ) : (
              <div className="space-y-4">
                {stats && Object.entries(stats.by_project)
                  .sort((a, b) => b[1] - a[1])
                  .map(([projectName, minutes]) => {
                    const hours = Math.floor(minutes / 60);
                    const mins = minutes % 60;
                    const percentage = (minutes / maxProjectMinutes) * 100;

                    return (
                      <div key={projectName} data-testid={`project-stat-${projectName}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-800">{projectName}</span>
                          <span className="text-gray-600">{hours}h {mins}m</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-cyan-500 to-teal-500 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </Card>

        {/* By Day */}
        <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0" data-testid="by-day-card">
          <div className="p-6">
            <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk' }}>Daily Breakdown</h3>
            {stats && Object.keys(stats.by_day).length === 0 ? (
              <p className="text-gray-600 text-center py-8">No entries this week</p>
            ) : (
              <div className="space-y-3">
                {stats && Object.entries(stats.by_day)
                  .sort((a, b) => b[0].localeCompare(a[0]))
                  .map(([date, minutes]) => {
                    const hours = Math.floor(minutes / 60);
                    const mins = minutes % 60;
                    const dayName = new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

                    return (
                      <div key={date} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0" data-testid={`day-stat-${date}`}>
                        <span className="font-medium text-gray-800">{dayName}</span>
                        <span className="text-gray-600">{hours}h {mins}m</span>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </Card>
      </div>

      <BottomNav active="week" />
    </div>
  );
};

export default Week;
